import { uniq } from 'es-toolkit';
import React, { FunctionComponent, ReactElement } from 'react';
import { connect } from 'react-redux';

import { ManualLoadDefinition, ModelCategory, ParapetManualLoadSettings } from '@paragon/contract-library';
import { getLayoutModelsWithPendingChanges, PathUtilities } from '@paragon/layout-models';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { RootState } from '../../../../../store';
import { DisplayNameProviderProps } from './PrimaryRowText';

type OwnProps = DisplayNameProviderProps<ParapetManualLoadSettings>;
type StateProps = ReturnType<typeof mapStateToProps>;

const ParapetLoadName: FunctionComponent<OwnProps & StateProps> = (props) => {
  if (!props.componentDesign) return <span>unknown component</span>;
  const parapetLoad = props.row.item;
  if (parapetLoad.name) {
    // @ts-expect-error https://github.com/DefinitelyTyped/DefinitelyTyped/discussions/53846
    return `${parapetLoad.name}` as ReactElement;
  }
  const memberType = getMemberTypeAbbreviation(parapetLoad);

  const loadCaseASmallestNonZeroMagnitude = getNonZeroExtremeForLoads(parapetLoad.loadCaseALoads, 'min');
  const loadCaseALargestNonZeroMagnitude = getNonZeroExtremeForLoads(parapetLoad.loadCaseALoads, 'max');
  const loadCaseBSmallestNonZeroMagnitude = getNonZeroExtremeForLoads(parapetLoad.loadCaseBLoads, 'min');
  const loadCaseBLargestNonZeroMagnitude = getNonZeroExtremeForLoads(parapetLoad.loadCaseBLoads, 'max');
  const loadCaseAMagnitudeString = getMagnitudeString(
    loadCaseASmallestNonZeroMagnitude,
    loadCaseALargestNonZeroMagnitude,
  );
  const loadCaseBMagnitudeString = getMagnitudeString(
    loadCaseBSmallestNonZeroMagnitude,
    loadCaseBLargestNonZeroMagnitude,
  );

  // @ts-expect-error https://github.com/DefinitelyTyped/DefinitelyTyped/discussions/53846
  return `${memberType} ${loadCaseAMagnitudeString} ${loadCaseBMagnitudeString}` as ReactElement;
};

const getMemberTypeAbbreviation = (parapetLoad: ParapetManualLoadSettings) => {
  const memberTypes = uniq(
    [...parapetLoad.loadCaseALoads, ...parapetLoad.loadCaseBLoads].map((load) => load.memberType),
  );
  if (memberTypes.length < 1) return 'None';
  if (memberTypes.length > 1) return 'Varied';

  const memberType = memberTypes[0];
  switch (memberType) {
    case 'TopChord':
      return 'TC';
    case 'BottomChord':
      return 'BC';
    case 'Web':
      return 'Web';
    case 'LeftEndVertical':
      return 'Left EV';
    case 'RightEndVertical':
      return 'Right EV';
    default:
      assertExhaustiveSwitch(memberType);
  }
};

const getMagnitudeString = (smallest: number | null, largest: number | null) => {
  if (smallest === null && largest === null) return '';
  if (smallest === null) return `(${largest})`;
  if (largest === null) return `(${smallest})`;
  return `(${smallest}, ${largest})`;
};

const getNonZeroExtremeForLoads = (loads: readonly ManualLoadDefinition[], minOrMax: 'min' | 'max') => {
  const operation = minOrMax === 'min' ? Math.min : Math.max;
  const loadMagnitudes = loads.flatMap((load) =>
    load.distribution === 'Distributed' ? [load.startMagnitude, load.endMagnitude] : [load.magnitude],
  );
  const nonZeroMagnitudes = loadMagnitudes.filter((magnitude) => magnitude !== 0);
  if (nonZeroMagnitudes.length > 0) {
    return operation(...nonZeroMagnitudes);
  }

  return null;
};

const mapStateToProps = (state: RootState, props: OwnProps) => {
  const pathGuids = PathUtilities.getGuids(props.row.rowId);
  let componentDesignGuid: string | undefined;
  for (const pathGuid of pathGuids) {
    const layoutModel = getLayoutModelsWithPendingChanges(state)[pathGuid];
    if (layoutModel && layoutModel.category === ModelCategory.ComponentDesign) {
      componentDesignGuid = pathGuid;
      break;
    }
  }
  return {
    componentDesign: componentDesignGuid != null ? state.componentDesigns.data[componentDesignGuid] : undefined,
  };
};

export default connect(mapStateToProps)(ParapetLoadName);
